import type { NodeType } from "@velizhanin-dev/bot-scenario"
import { useReactFlow, useViewport } from "@xyflow/react"
import { Flag, LayoutDashboard, Maximize, Minus, Plus } from "lucide-react"
import type { ScenarioDraft } from "@/entities/bot-builder"
import { AddBlockMenu } from "@/features/edit-scenario-draft"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Separator } from "@/shared/ui/separator"
import { ToolButton } from "@/shared/ui/tool-button"
import { BlockSearch } from "./block-search"

const ZOOM_ANIMATION = { duration: 200 }

/** Плавающая панель поверх холста: та же подложка, что у переключателя «Список / Схема». */
export const FLOATING_PANEL_CLASS =
	"rounded-2xl border border-border bg-card/90 shadow-2xl shadow-black/40 backdrop-blur-md"

interface CanvasToolbarProps {
	draft: ScenarioDraft
	flowId: string
	addBlockHint: string
	onAddBlock: (type: Exclude<NodeType, "start">) => void
	onPickNode: (nodeId: string) => void
	onArrange: () => void
}

/** Низ холста: добавить блок и вернуться к старту; масштаб справа, поиск и порядок слева. */
export function CanvasToolbar({
	draft,
	flowId,
	addBlockHint,
	onAddBlock,
	onPickNode,
	onArrange,
}: CanvasToolbarProps) {
	const { zoomIn, zoomOut, fitView } = useReactFlow()
	const { zoom } = useViewport()
	const flowNodes = draft.nodes.filter((node) => node.flowId === flowId)
	const startNode =
		flowNodes.find((node) => node.type === "start") ??
		[...flowNodes].sort((a, b) => a.positionX - b.positionX)[0]

	return (
		<>
			<div className="pointer-events-none absolute inset-x-0 bottom-3 z-10 flex justify-center px-3">
				<div className={cn(FLOATING_PANEL_CLASS, "pointer-events-auto flex items-center gap-1 p-1.5")}>
					<AddBlockMenu onPick={onAddBlock} hint={addBlockHint} align="center" />
					<Separator orientation="vertical" className="mx-1 h-5" />
					<BlockSearch draft={draft} onPick={onPickNode} triggerClassName="size-9" />
					<ToolButton label="Упорядочить блоки" className="size-9" onClick={onArrange}>
						<LayoutDashboard />
					</ToolButton>
					<ToolButton
						label="К старту воронки"
						className="size-9"
						disabled={!startNode}
						onClick={() => startNode && onPickNode(startNode.id)}
					>
						<Flag />
					</ToolButton>
				</div>
			</div>

			<div
				className={cn(
					FLOATING_PANEL_CLASS,
					"absolute right-3 bottom-3 z-10 flex flex-col items-center gap-0.5 p-1",
				)}
			>
				<ToolButton label="Приблизить" className="size-9 [&_svg]:size-4" onClick={() => void zoomIn(ZOOM_ANIMATION)}>
					<Plus />
				</ToolButton>
				<ToolButton label="Отдалить" className="size-9 [&_svg]:size-4" onClick={() => void zoomOut(ZOOM_ANIMATION)}>
					<Minus />
				</ToolButton>
				<Button
					type="button"
					variant="ghost"
					title="Показать все блоки"
					className="hidden h-10 w-9 flex-col items-center gap-0.5 rounded-xl p-0 text-[10px] text-muted-foreground tabular-nums hover:text-foreground sm:flex"
					onClick={() => void fitView({ padding: 0.2, maxZoom: 1, ...ZOOM_ANIMATION })}
				>
					<Maximize className="size-3.5" />
					{Math.round(zoom * 100)}%
				</Button>
			</div>
		</>
	)
}
